import React, { useState, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { editLockMotionSettings, fetchLockMotionSettings } from "actions/Units";
import Tooltip from "components/Tooltip";
import NumberInput from "components/NumberInput";
import SelectInput from "components/SelectInput";
import ButtonContained from "components/ButtonContained";
import LoadingIndicator from "components/LoadingIndicator";
import * as constants from "constants";

const presets = {
  one: {
    longMotionDurationDesired: constants.LOCK_MOTION_SETTINGS_PRESET_1_LONG_MOTION_DURATION,
    shortMotionDurationDesired: constants.LOCK_MOTION_SETTINGS_PRESET_1_SHORT_MOTION_DURATION,
    shortMotionCountsDesired: constants.LOCK_MOTION_SETTINGS_PRESET_1_SHORT_MOTION_COUNTS,
  },
  two: {
      longMotionDurationDesired: constants.LOCK_MOTION_SETTINGS_PRESET_2_LONG_MOTION_DURATION,
      shortMotionDurationDesired: constants.LOCK_MOTION_SETTINGS_PRESET_2_SHORT_MOTION_DURATION,
      shortMotionCountsDesired: constants.LOCK_MOTION_SETTINGS_PRESET_2_SHORT_MOTION_COUNTS,
  },
  three: {
      longMotionDurationDesired: constants.LOCK_MOTION_SETTINGS_PRESET_3_LONG_MOTION_DURATION,
      shortMotionDurationDesired: constants.LOCK_MOTION_SETTINGS_PRESET_3_SHORT_MOTION_DURATION,
      shortMotionCountsDesired: constants.LOCK_MOTION_SETTINGS_PRESET_3_SHORT_MOTION_COUNTS,
  },
  four: {
      longMotionDurationDesired: constants.LOCK_MOTION_SETTINGS_PRESET_4_LONG_MOTION_DURATION,
      shortMotionDurationDesired: constants.LOCK_MOTION_SETTINGS_PRESET_4_SHORT_MOTION_DURATION,
      shortMotionCountsDesired: constants.LOCK_MOTION_SETTINGS_PRESET_4_SHORT_MOTION_COUNTS,
  },
};

const LockSettingsMotionSettingsModal = ({ lock }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  
  const motionSettingsPresetRef = useRef(null);
  const longMotionDurationRef = useRef(null);
  const shortMotionDurationRef = useRef(null);
  const shortMotionCountsRef = useRef(null);
  const motionCountsRef = useRef(null);
  const motionEnabledRef = useRef(null);
  const overrideSiteSettingRef = useRef(null);
  const authUser = useSelector(state => state.auth.authUser);
  const fetchedLock = useSelector(state => state.units.unitObject?.unit?.locks.find(l => l.id === lock.id));
  const lockMotionSettings = useSelector(state => state.units.lockMotionSettings);
  const [hasChanges, setHasChanges] = useState(false);
  const [localLockMotionSettings, setLocalLockMotionSettings] = useState(null);
  const [motionSettingsPreset, setMotionSettingsPreset] = useState("custom");

  const isBackMotionFeatureEnabled =
      authUser?.featureflagAssignments?.includes('motion_settings_condensed_1A') ||
      authUser?.featureflagAssignments?.includes('motion_settings_condensed_3E') ||
      authUser?.featureflagAssignments?.includes('motion_settings_condensed_4E');

  useEffect(() => {
    dispatch(fetchLockMotionSettings(lock.uuid));
  }, [])

  useEffect(() => {
    if (lockMotionSettings) {
      setLocalLockMotionSettings(lockMotionSettings);
    }
  }, [lockMotionSettings])

  useEffect(() => {
    if (localLockMotionSettings) {
      // Check if the desired values match any preset
      const matchedPreset = Object.keys(presets).find((key) => {
        const preset = presets[key];
        return (
          preset.longMotionDurationDesired === localLockMotionSettings.longMotionDurationDesired &&
          preset.shortMotionDurationDesired === localLockMotionSettings.shortMotionDurationDesired &&
          preset.shortMotionCountsDesired === localLockMotionSettings.shortMotionCountsDesired
        );
      });
  
      // Set the preset if a match is found, otherwise default to "custom"
      setMotionSettingsPreset(matchedPreset || "custom");
    }

    if (localLockMotionSettings && lockMotionSettings) {
      const isChanged = JSON.stringify(localLockMotionSettings) !== JSON.stringify(lockMotionSettings);
      setHasChanges(isChanged);
    }
  }, [localLockMotionSettings, lockMotionSettings]);

  const handleInputChange = (field, value) => {
    setLocalLockMotionSettings((prevSettings) => ({
      ...prevSettings,
      [field]: value,
    }));
  };

  const handleSelectPreset = (value) => {
    setMotionSettingsPreset(value);
    if (value !== "custom" && presets[value]) {
      setLocalLockMotionSettings((prevSettings) => ({
        ...prevSettings,
        ...presets[value],
      }));
    }
  };

  const handleSaveMotionSettings = () => {
    let newSettings = { ...localLockMotionSettings };
    if (!newSettings.overrideSiteSetting) {
      newSettings.motionEnabled = lockMotionSettings.motionEnabled;
      newSettings.longMotionDurationDesired = lockMotionSettings.longMotionDurationDesired;
      newSettings.shortMotionDurationDesired = lockMotionSettings.shortMotionDurationDesired;
      newSettings.shortMotionCountsDesired = lockMotionSettings.shortMotionCountsDesired;
    }
    if (!newSettings.motionEnabled) {
      newSettings.longMotionDurationDesired = isBackMotionFeatureEnabled ? 0 : lockMotionSettings.longMotionDurationDesired;
      newSettings.shortMotionDurationDesired = isBackMotionFeatureEnabled ? 0 : lockMotionSettings.shortMotionDurationDesired;
      newSettings.shortMotionCountsDesired = lockMotionSettings.shortMotionCountsDesired;
    }
    dispatch(
      editLockMotionSettings(
        {
          ...newSettings,
          uuid: fetchedLock.uuid
        },
        {
          successMessage: intl.formatMessage({ id: "notifications.success.edit" }),
        }
      )
    )
  }

  // Individual validation logic for each input
  const hasLongMotionDurationError =
  (isBackMotionFeatureEnabled || localLockMotionSettings?.overrideSiteSetting)
  ?
  false
  :
  localLockMotionSettings?.longMotionDurationDesired < constants.LOCK_MOTION_SETTINGS_VALIDATION_LONG_MOTION_DURATION_MIN ||
  localLockMotionSettings?.longMotionDurationDesired > constants.LOCK_MOTION_SETTINGS_VALIDATION_LONG_MOTION_DURATION_MAX;

  const hasShortMotionDurationError =
  (isBackMotionFeatureEnabled || localLockMotionSettings?.overrideSiteSetting)
  ?
  false
  :
  localLockMotionSettings?.shortMotionDurationDesired < constants.LOCK_MOTION_SETTINGS_VALIDATION_SHORT_MOTION_DURATION_MIN ||
  localLockMotionSettings?.shortMotionDurationDesired > constants.LOCK_MOTION_SETTINGS_VALIDATION_SHORT_MOTION_DURATION_MAX;

  const hasShortMotionCountsError =
  localLockMotionSettings?.overrideSiteSetting
  ?
  false
  :
  localLockMotionSettings?.shortMotionCountsDesired < constants.LOCK_MOTION_SETTINGS_VALIDATION_SHORT_MOTION_COUNTS_MIN ||
  localLockMotionSettings?.shortMotionCountsDesired > constants.LOCK_MOTION_SETTINGS_VALIDATION_SHORT_MOTION_COUNTS_MAX;

  // Combined validation logic for the save button
  const hasValidationErrors = hasLongMotionDurationError || hasShortMotionDurationError || hasShortMotionCountsError;

  const showPendingWarning =
    lockMotionSettings?.longMotionDurationDesired !== lockMotionSettings?.longMotionDurationActual ||
    lockMotionSettings?.shortMotionDurationDesired !== lockMotionSettings?.shortMotionDurationActual ||
    lockMotionSettings?.shortMotionCountsDesired !== lockMotionSettings?.shortMotionCountsActual;

  return (
    fetchedLock && lockMotionSettings ? <div className="min-h-[300px] max-h-[60vh] px-10 flex flex-col justify-between">
      <div className="grid grid-cols-2 gap-8 py-4 px-[1px]">
        <>
          <div ref={overrideSiteSettingRef}>
          <SelectInput
              id="Lock-Motion-Settings-Override-Site-Setting-Input"
              dataCy="Lock-Motion-Settings-Override-Site-Setting-Input"
              value={localLockMotionSettings?.overrideSiteSetting ?? false}
              placeholder={intl.formatMessage({ id: "form.overrideSiteSetting" })}
              onChange={(e) => handleInputChange("overrideSiteSetting", e.target.value === "true")}
              options={[
                {
                  name: intl.formatMessage({ id: "form.enable" }),
                  value: true
                },
                {
                  name: intl.formatMessage({ id: "form.disable" }),
                  value: false
                },
              ]}
            />
          </div>
          <Tooltip
            anchorRef={overrideSiteSettingRef}
            text={intl.formatMessage({ id: "form.overrideSiteSettingDesc" })}
          />
        </>
        {localLockMotionSettings?.overrideSiteSetting && <>
          <div ref={motionEnabledRef}>
          <SelectInput
              id="Lock-Motion-Settings-Motion-Enabled-Input"
              dataCy="Lock-Motion-Settings-Motion-Enabled-Input"
              value={localLockMotionSettings?.motionEnabled ?? false}
              placeholder={intl.formatMessage({ id: "form.motionEnabled" })}
              onChange={(e) => handleInputChange("motionEnabled", e.target.value === "true")}
              options={[
                {
                  name: intl.formatMessage({ id: "form.enable" }),
                  value: true
                },
                {
                  name: intl.formatMessage({ id: "form.disable" }),
                  value: false
                },
              ]}
            />
          </div>
          <Tooltip
            anchorRef={motionEnabledRef}
            text={intl.formatMessage({ id: "form.motionEnabledDesc" })}
          />
        </>}
        {isBackMotionFeatureEnabled && localLockMotionSettings?.overrideSiteSetting && localLockMotionSettings?.motionEnabled && <>
          <div ref={motionCountsRef}>
            <SelectInput
              id="Lock-Motion-Settings-Back-Motion-Count-Input"
              dataCy="Lock-Motion-Settings-Back-Motion-Count-Input"
              value={localLockMotionSettings?.shortMotionCountsDesired ?? ""}
              placeholder={intl.formatMessage({ id: "form.motionCount" })}
              onChange={(e) => handleInputChange("shortMotionCountsDesired", e.target.value)}
              options={[
                { name: 1, value: 1 },
                { name: 2, value: 2 },
                { name: 3, value: 3 },
                { name: 4, value: 4 },
              ]}
            />
          </div>
          <Tooltip
            anchorRef={motionCountsRef}
            text={intl.formatMessage({ id: "form.motionCountDesc" })}
          />
        </>}
        {!isBackMotionFeatureEnabled && localLockMotionSettings?.overrideSiteSetting && localLockMotionSettings?.motionEnabled && <>
          <div ref={motionSettingsPresetRef}>
            <SelectInput
              id="Lock-Motion-Settings-Preset-Input"
              dataCy="Lock-Motion-Settings-Preset-Input"
              value={motionSettingsPreset}
              placeholder={intl.formatMessage({ id: "form.shortMotionDuration" })}
              onChange={(e) => handleSelectPreset(e.target.value)}
              options={[
                { name: intl.formatMessage({ id: "form.motionpreset.custom" }), value: "custom" },
                { name: intl.formatMessage({ id: "form.motionpreset.one" }), value: "one" },
                { name: intl.formatMessage({ id: "form.motionpreset.two" }), value: "two" },
                { name: intl.formatMessage({ id: "form.motionpreset.three" }), value: "three" },
                { name: intl.formatMessage({ id: "form.motionpreset.four" }), value: "four" },
              ]}
            />
          </div>
          <Tooltip
            anchorRef={motionSettingsPresetRef}
            text={intl.formatMessage({ id: "form.motionPresetDesc" })}
          />
        </>}
        {!isBackMotionFeatureEnabled && localLockMotionSettings?.overrideSiteSetting && localLockMotionSettings?.motionEnabled && <>
          <div ref={longMotionDurationRef}>
            <NumberInput
              id="Lock-Motion-Settings-Long-Motion-Duration-Input"
              dataCy="Lock-Motion-Settings-Long-Motion-Duration-Input"
              placeholder={intl.formatMessage({ id: "form.longMotionDuration" })}
              onChange={(e) => handleInputChange("longMotionDurationDesired", e.target.value)}
              value={localLockMotionSettings?.longMotionDurationDesired ?? ""}
              isDisabled={motionSettingsPreset !== "custom"}
              hasError={hasLongMotionDurationError}
              min={constants.LOCK_MOTION_SETTINGS_VALIDATION_LONG_MOTION_DURATION_MIN}
              max={constants.LOCK_MOTION_SETTINGS_VALIDATION_LONG_MOTION_DURATION_MAX}
            />
          </div>
          <Tooltip
            anchorRef={longMotionDurationRef}
            text={intl.formatMessage({ id: "form.longMotionDurationDesc" })}
          />
        </>}
        {!isBackMotionFeatureEnabled && localLockMotionSettings?.overrideSiteSetting && localLockMotionSettings?.motionEnabled && <>
          <div ref={shortMotionDurationRef}>
            <NumberInput
              id="Lock-Motion-Settings-Short-Motion-Duration-Input"
              dataCy="Lock-Motion-Settings-Short-Motion-Duration-Input"
              placeholder={intl.formatMessage({ id: "form.shortMotionDuration" })}
              onChange={(e) => handleInputChange("shortMotionDurationDesired", e.target.value)}
              value={localLockMotionSettings?.shortMotionDurationDesired ?? ""}
              isDisabled={motionSettingsPreset !== "custom"}
              hasError={hasShortMotionDurationError}
              min={constants.LOCK_MOTION_SETTINGS_VALIDATION_SHORT_MOTION_DURATION_MIN}
              max={constants.LOCK_MOTION_SETTINGS_VALIDATION_SHORT_MOTION_DURATION_MAX}
            />
          </div>
          <Tooltip
            anchorRef={shortMotionDurationRef}
            text={intl.formatMessage({ id: "form.shortMotionDurationDesc" })}
          />
        </>}
        {!isBackMotionFeatureEnabled && localLockMotionSettings?.overrideSiteSetting && localLockMotionSettings?.motionEnabled && <>
          <div ref={shortMotionCountsRef}>
            <NumberInput
              id="Lock-Motion-Settings-Short-Motion-Counts-Input"
              dataCy="Lock-Motion-Settings-Short-Motion-Counts-Input"
              placeholder={intl.formatMessage({ id: "form.shortMotionCounts" })}
              onChange={(e) => handleInputChange("shortMotionCountsDesired", e.target.value)}
              value={localLockMotionSettings?.shortMotionCountsDesired ?? ""}
              isDisabled={motionSettingsPreset !== "custom"}
              hasError={hasShortMotionCountsError}
              min={constants.LOCK_MOTION_SETTINGS_VALIDATION_SHORT_MOTION_COUNTS_MIN}
              max={constants.LOCK_MOTION_SETTINGS_VALIDATION_SHORT_MOTION_COUNTS_MAX}
            />
          </div>
          <Tooltip
            anchorRef={shortMotionCountsRef}
            text={intl.formatMessage({ id: "form.shortMotionCountsDesc" })}
          />
        </>}
      </div>
      {showPendingWarning && (
        <div>
          <p className="tw-paragraph-reg text-[var(--color-alert)] pb-4">{intl.formatMessage({ id: "form.motion.pendingWarning" })}</p>
        </div>
      )}
      <div>
        <ButtonContained
          id="Lock-Settings-Motion-Settings-Save-Changes-Button"
          dataCy="Lock-Settings-Motion-Settings-Save-Changes-Button"
          text={intl.formatMessage({ id: "comp.button.saveChanges" })}
          type="default"
          disabled={!hasChanges || hasValidationErrors}
          onClick={handleSaveMotionSettings}
        />
      </div>
    </div> : <div className="min-h-[300px] flex justify-center">
      <LoadingIndicator />
    </div>
  )
};

export default LockSettingsMotionSettingsModal;